/**
 * Whether the signed-in user is in the admin tier, for the screens that only
 * show or hide something on it: the sidebar's « Catalogue » entry
 * (modules/nav/Sidebar.tsx), the home page's admin parts — the platform
 * strip, the deletion of a project (HomePage.tsx) — and the catalog index, which sends
 * everyone else home (components/catalogIndex). Ergonomics only — the catalog
 * API stays open, and the permission policy decides what it lists.
 *
 * One `GET /api/waadoo-platform/me` for both (the sidebar and the home page
 * are mounted at once), through the `sharedLoad.ts` pattern. Not `/overview`:
 * that one reads the cluster's capacity for an admin, and the sidebar is on
 * every page.
 *
 * The cache hangs off the app's `FetchApi` instance rather than the module:
 * the app has one, so every screen shares the same answer, while each test,
 * which mocks its own, starts from an empty cache.
 */

import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/frontend-plugin-api';
import {
  createSharedLoad,
  type SharedLoad,
} from '../projectEnvironments/sharedLoad';
import { fetchIsAdmin } from './platform';

/**
 * How long one answer is reused. The backend decides from the caller's
 * ownership refs, which are frozen at sign-in: a longer TTL would not be
 * wrong, this one only bounds how long a stale page keeps an old answer.
 */
export const ADMIN_TTL_MS = 5 * 60_000;

const loads = new WeakMap<FetchApi, SharedLoad<boolean>>();

/** The shared read behind {@link useIsAdmin}. Rejects when the request fails. */
export function loadIsAdmin(options: {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
}): Promise<boolean> {
  let shared = loads.get(options.fetchApi);
  if (!shared) {
    shared = createSharedLoad<boolean>(ADMIN_TTL_MS);
    loads.set(options.fetchApi, shared);
  }
  return shared.load(() => fetchIsAdmin(options));
}

/**
 * Where the admin check stands: `pending` until the backend answers, then
 * `admin` or `member`, or `unknown` when the request failed.
 */
export type AdminCheck = 'pending' | 'admin' | 'member' | 'unknown';

/**
 * The admin check with its pending state told apart from its failure: a
 * screen that must show nothing until it knows (the catalog index) waits on
 * `pending`, and treats `unknown` as `member` — fail closed.
 */
export function useAdminCheck(): AdminCheck {
  const fetchApi = useApi(fetchApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const [check, setCheck] = useState<AdminCheck>('pending');

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const admin = await loadIsAdmin({ fetchApi, discoveryApi });
        if (active) setCheck(admin ? 'admin' : 'member');
      } catch {
        if (active) setCheck('unknown');
      }
    };
    // `load` turns its own failure into state, so nothing can reject.
    void load();
    return () => {
      active = false;
    };
  }, [fetchApi, discoveryApi]);

  return check;
}

/**
 * True or false once the backend has answered; undefined while it loads and
 * when the request failed. Callers hide the admin-only entry in both cases:
 * fail closed, it is only a shortcut.
 */
export function useIsAdmin(): boolean | undefined {
  const check = useAdminCheck();
  if (check === 'admin') return true;
  if (check === 'member') return false;
  return undefined;
}
